/** Client-side helpers for /demo/try telemetry (mirrors benchmark sessionStorage pattern). */

const SESSION_KEY = 'djDemoSessionId';
const IDENTITY_KEY = 'djDemoIdentity';

export type DemoIdentity = {
  email: string;
  name?: string;
};

export type DemoEventType =
  | 'session_start'
  | 'territory_filter'
  | 'hcp_open'
  | 'soft_ask_shown'
  | 'soft_ask_submit'
  | 'soft_ask_skip'
  | 'pdf_gate_shown'
  | 'pdf_identity_submit'
  | 'pdf_download'
  | 'interaction_span';

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `demo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getDemoSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return randomId();
  }
}

export function getDemoIdentity(): DemoIdentity | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(IDENTITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DemoIdentity;
    if (!parsed?.email || typeof parsed.email !== 'string') return null;
    return {
      email: parsed.email.trim(),
      name: parsed.name?.trim() || undefined,
    };
  } catch {
    return null;
  }
}

export function setDemoIdentity(identity: DemoIdentity): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      IDENTITY_KEY,
      JSON.stringify({
        email: identity.email.trim(),
        name: identity.name?.trim() || undefined,
      }),
    );
  } catch {
    /* ignore */
  }
}

export function isValidWorkEmail(email: string): boolean {
  const trimmed = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return false;
  const domain = trimmed.split('@')[1]?.toLowerCase() || '';
  return domain.length > 0;
}

function browserMeta(): { device_type: string; browser: string } {
  if (typeof navigator === 'undefined') {
    return { device_type: 'unknown', browser: 'unknown' };
  }
  const ua = navigator.userAgent;
  const device_type = /Mobi|Android/i.test(ua) ? 'mobile' : 'desktop';
  let browser = 'other';
  if (ua.includes('Firefox')) browser = 'firefox';
  else if (ua.includes('Edg/')) browser = 'edge';
  else if (ua.includes('Chrome')) browser = 'chrome';
  else if (ua.includes('Safari')) browser = 'safari';
  return { device_type, browser };
}

export async function trackDemoEvent(
  eventType: DemoEventType,
  meta?: Record<string, unknown>,
): Promise<void> {
  if (typeof window === 'undefined') return;
  const sessionId = getDemoSessionId();
  const identity = getDemoIdentity();
  const params = new URLSearchParams(window.location.search);
  const source =
    params.get('utm_source') ||
    params.get('source') ||
    (document.referrer ? 'referrer' : 'direct');

  const body = {
    session_id: sessionId,
    event_type: eventType,
    email: identity?.email || null,
    name: identity?.name || null,
    source,
    landing_path: window.location.pathname + window.location.search,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent.slice(0, 400),
    ...browserMeta(),
    meta: meta || null,
  };

  try {
    await fetch('/api/demo-events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (err) {
    console.warn('demo event failed', err);
  }
}
